import { ArrowsRotateRight, PaperPlane } from "@gravity-ui/icons";
import { Button, Card, ProgressBar, Table } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  ChannelChip,
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  MessageStatusChip,
  StateChip,
} from "../../components/ui";
import { shortId, useFormat } from "../../lib/format";
import { useMessages } from "../../lib/queries";

export function MessagesTab({ appId }: { appId: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const messages = useMessages(appId);

  if (messages.isPending) return <LoadingBlock />;
  if (messages.isError) return <ErrorBlock error={messages.error} onRetry={() => messages.refetch()} />;
  const rows = messages.data.pages.flatMap((p) => p.items);

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyBlock icon={PaperPlane} title={t("messages.empty")} hint={t("messages.emptyHint")} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" isPending={messages.isFetching} onPress={() => messages.refetch()}>
          <ArrowsRotateRight />
          {t("common.refresh")}
        </Button>
      </div>
      <Table>
        <Table.ScrollContainer>
          <Table.Content
            aria-label={t("appDetail.messages")}
            className="min-w-[860px]"
            onRowAction={(key) => navigate(`/apps/${appId}/messages/${String(key)}`)}
          >
            <Table.Header>
              <Table.Column isRowHeader>{t("messages.message")}</Table.Column>
              <Table.Column>{t("common.status")}</Table.Column>
              <Table.Column>{t("messages.channels")}</Table.Column>
              <Table.Column>{t("messages.progress")}</Table.Column>
              <Table.Column>{t("messages.priority")}</Table.Column>
              <Table.Column>{t("messages.mode")}</Table.Column>
              <Table.Column>{t("messages.createdAt")}</Table.Column>
            </Table.Header>
            <Table.Body>
              {rows.map((m) => {
                const done = m.counts.sent + m.counts.failed + m.counts.skipped;
                const pct = m.counts.total ? Math.round((done / m.counts.total) * 100) : 0;
                return (
                  <Table.Row key={m.id} id={m.id} className="cursor-pointer">
                    <Table.Cell>
                      <span className="flex max-w-[280px] flex-col">
                        <span className="truncate text-sm font-medium">{m.preview ?? "—"}</span>
                        <span className="font-mono text-xs text-muted">{shortId(m.id)}</span>
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <MessageStatusChip status={m.status} />
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-1">
                        {m.channels.map((c) => (
                          <ChannelChip key={c} channel={c} />
                        ))}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex min-w-36 items-center gap-3">
                        <ProgressBar
                          aria-label={t("messages.progress")}
                          value={pct}
                          className="w-20"
                          size="sm"
                        >
                          <ProgressBar.Track>
                            <ProgressBar.Fill />
                          </ProgressBar.Track>
                        </ProgressBar>
                        <span className="text-xs text-muted tabular-nums">
                          {m.counts.sent}/{m.counts.total}
                          {m.counts.failed ? (
                            <span className="text-danger"> · {m.counts.failed}✕</span>
                          ) : null}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-sm">{t(`priority.${m.priority}`)}</Table.Cell>
                    <Table.Cell>
                      <StateChip
                        ok={m.test_mode ? "warn" : true}
                        label={m.test_mode ? t("keys.test") : t("keys.live")}
                      />
                    </Table.Cell>
                    <Table.Cell className="text-sm whitespace-nowrap">
                      {fmt.dateTime(m.created_at)}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
      {messages.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            isPending={messages.isFetchingNextPage}
            onPress={() => messages.fetchNextPage()}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
